import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EventScannerNav } from '../../components/scanner';
import { Container, Row, Col, Card, Button, Alert, ProgressBar } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { StatsChart } from '../../components/scanner';
import { apiService } from '../../services/api';
import { EventStats } from '../../types';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './EventStatsPage.scss';

const EventStatsPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<EventStats | null>(null);

  const fetchStats = useCallback(async () => {
    if (!eventSlug) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.scanner.getEventStats(eventSlug);
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [eventSlug]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleBackToDashboard = useCallback(() => {
    navigate('/scanner/');
  }, [navigate]);

  const statCards = useMemo(() => {
    if (!stats) return null;

    return (
      <Row className="mb-3 g-3">
        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card stat-card-primary">
            <Card.Body className="p-3">
              <div className="stat-icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Passes</div>
                <div className="stat-number">{stats.total_passes.toLocaleString()}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card stat-card-success">
            <Card.Body className="p-3">
              <div className="stat-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-label">Scanned</div>
                <div className="stat-number text-success">{stats.scanned_passes.toLocaleString()}</div>
                <div className="stat-progress mt-2">
                  <ProgressBar 
                    now={stats.scan_percentage} 
                    variant="success" 
                    className="stat-progress-bar"
                    label={`${stats.scan_percentage}%`}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card stat-card-warning">
            <Card.Body className="p-3">
              <div className="stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-content">
                <div className="stat-label">Pending</div>
                <div className="stat-number text-warning">{stats.pending_passes.toLocaleString()}</div>
                <div className="stat-progress mt-2">
                  <ProgressBar 
                    now={stats.total_passes > 0 ? (stats.pending_passes / stats.total_passes) * 100 : 0} 
                    variant="warning" 
                    className="stat-progress-bar"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }, [stats]);

  if (loading) {
    return (
      <Container className="event-stats-page py-3 scanner-page-with-nav">
        <SkeletonLoader />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="event-stats-page py-3 scanner-page-with-nav">
        <Alert variant="danger" className="mb-3">{error}</Alert>
        <Button variant="primary" onClick={handleBackToDashboard}>
          <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
        </Button>
        <EventScannerNav />
      </Container>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <>
      <Container className="event-stats-page py-3 scanner-page-with-nav">
        {/* Header */}
        <div className="scanner-header-wrapper mb-4">
          <div className="scanner-header">
            <button
              className="btn btn-glass btn-icon"
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <div className="header-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <div className="header-text">
                <h1 className="text-primary mb-2">Event Statistics</h1>
                <h4 className="mb-2">{stats.event.title}</h4>
                <p className="text-muted mb-0">Real-time scanning statistics and analytics</p>
              </div>
            </div>
            <div className="btn btn-glass btn-icon invisible">
              <i className="fas fa-arrow-left"></i>
            </div>
          </div>
        </div>

        {/* Overview Statistics */}
        {statCards}

        {/* Hourly Scan Activity */}
        <Row className="mb-3">
          <Col lg={12}>
            <Card className="glass-card">
              <Card.Header className="p-3">
                <Card.Title className="card-title mb-0">
                  <i className="fas fa-chart-line me-2"></i>Hourly Scan Activity
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {stats.hourly_scans && stats.hourly_scans.length > 0 ? (
                  <StatsChart data={stats.hourly_scans} title="" color="var(--bs-primary)" />
                ) : (
                  <div className="empty-state text-center py-4">
                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                    <p className="text-muted mb-0">No hourly scan data available</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Category Breakdown & Scanner Performance */}
        <Row className="mb-3 g-3">
          <Col lg={6}>
            <Card className="glass-card">
              <Card.Header className="p-3">
                <Card.Title className="card-title mb-0">
                  <i className="fas fa-tags me-2"></i>Category Breakdown
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {stats.category_stats && stats.category_stats.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th className="text-end">Total</th>
                          <th className="text-end">Scanned</th>
                          <th className="text-end">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.category_stats.map((cat, index) => (
                          <tr key={index}>
                            <td>
                              {cat.category__tag && (
                                <span className="badge bg-secondary me-2">{cat.category__tag}</span>
                              )}
                              {cat.category__name || 'Uncategorized'}
                            </td>
                            <td className="text-end">{cat.total.toLocaleString()}</td>
                            <td className="text-end text-success">{cat.scanned.toLocaleString()}</td>
                            <td className="text-end text-warning">{cat.pending.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state text-center py-3">
                    <i className="fas fa-tags fa-2x text-muted mb-2"></i>
                    <p className="text-muted small mb-0">No category data available</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="glass-card">
              <Card.Header className="p-3">
                <Card.Title className="card-title mb-0">
                  <i className="fas fa-user-check me-2"></i>Scanner Performance
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {stats.scanner_stats && stats.scanner_stats.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Scanner</th>
                          <th className="text-end">Scans</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.scanner_stats
                          .sort((a, b) => (b.scan_count || 0) - (a.scan_count || 0))
                          .map((scanner, index) => (
                            <tr key={index}>
                              <td>
                                <i className="fas fa-user me-2 text-muted"></i>
                                {scanner.scanned_by_email || 'Unknown'}
                              </td>
                              <td className="text-end">
                                <span className="badge bg-primary">{scanner.scan_count?.toLocaleString() || 0}</span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state text-center py-3">
                    <i className="fas fa-user-check fa-2x text-muted mb-2"></i>
                    <p className="text-muted small mb-0">No scanner data available</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Activity */}
        <Row>
          <Col lg={12}>
            <Card className="glass-card">
              <Card.Header className="p-3">
                <Card.Title className="card-title mb-0">
                  <i className="fas fa-history me-2"></i>Recent Activity
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {stats.recent_activity && stats.recent_activity.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Category</th>
                          <th>Scanner</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_activity.map((activity) => (
                          <tr key={activity.id}>
                            <td>
                              <i className="fas fa-user me-2 text-muted"></i>
                              {activity.name}
                            </td>
                            <td>{activity.email}</td>
                            <td>
                              <span className="badge bg-secondary">{activity.category}</span>
                            </td>
                            <td>{activity.scanned_by || 'N/A'}</td>
                            <td>
                              <small className="text-muted">
                                {activity.scanned_on 
                                  ? new Date(activity.scanned_on).toLocaleString() 
                                  : '-'}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state text-center py-4">
                    <i className="fas fa-history fa-3x text-muted mb-3"></i>
                    <p className="text-muted mb-0">No recent activity</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <EventScannerNav />
    </>
  );
};

export default EventStatsPage;

